import { Loader as GoogleMaps } from "@googlemaps/js-api-loader";
import { activeSiteConfig } from "@config/site/site-config";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

let googleMapsLoader: GoogleMaps | null = null;
let loadPromise: Promise<typeof google> | null = null;
let isLoaded = false;

const getGoogleMapsLoader = (locale?: string): GoogleMaps => {
    if (googleMapsLoader) {
        return googleMapsLoader;
    }

    const { country } = activeSiteConfig;

    googleMapsLoader = new GoogleMaps({
        apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY,
        region: country,
        language: locale,
        libraries: ["geometry", "places"],
        id: "google-maps-script",
    });

    return googleMapsLoader;
};

const loadGoogleMaps = async (locale?: string): Promise<typeof google> => {
    if (isLoaded && window.google?.maps) {
        return Promise.resolve(window.google);
    }

    if (loadPromise !== null) {
        return loadPromise;
    }

    const loader = getGoogleMapsLoader(locale);

    loadPromise = loader
        .load()
        .then((google) => {
            isLoaded = true;
            return google;
        })
        .catch((error) => {
            loadPromise = null;
            throw error;
        });

    return loadPromise;
};

const CACHE_TTL = 30 * 60 * 1000;
const geocodeCache = new Map<string, { data: google.maps.GeocoderResult[]; timestamp: number }>();

const createCacheKey = (address: string, country?: string): string => {
    return `${address.toLowerCase().trim()}${country ? `_${country}` : ""}`;
};

const isValidCache = (entry: any): boolean => {
    return entry && Date.now() - entry.timestamp < CACHE_TTL;
};

const performGeocode = async (
    address: string,
    componentRestrictions?: { country: string }
): Promise<google.maps.GeocoderResult[]> => {
    const geocoder = new google.maps.Geocoder();
    const response = await geocoder.geocode({
        address,
        componentRestrictions,
    });

    return response.results;
};

export const googleMapsUtils = {
    loadMaps: loadGoogleMaps,
    isLoaded: (): boolean => isLoaded && !!window.google?.maps,
    geocodeAddress: async (
        address: string,
        componentRestrictions?: { country: string }
    ): Promise<google.maps.GeocoderResult[]> => {
        if (!address || address.trim() === "") {
            return [];
        }

        await loadGoogleMaps();

        const cacheKey = createCacheKey(address, componentRestrictions?.country);

        const cachedResult = geocodeCache.get(cacheKey);
        if (isValidCache(cachedResult)) {
            return cachedResult.data;
        }

        try {
            const results = await performGeocode(address, componentRestrictions);
            geocodeCache.set(cacheKey, {
                data: results,
                timestamp: Date.now(),
            });

            return results;
        } catch (error) {
            if (error.code === "ZERO_RESULTS") {
                geocodeCache.set(cacheKey, {
                    data: [],
                    timestamp: Date.now(),
                });
                return [];
            }

            Logger.error(ServiceType.WEB, "Geocoding error", {
                tag: LogTag.ERROR,
                error,
                address,
            });
            throw error;
        }
    },
    initializeAutocomplete: async (
        inputElement: HTMLInputElement,
        options: any,
        onPlaceSelected: (place: any) => void
    ): Promise<google.maps.places.Autocomplete | null> => {
        try {
            await loadGoogleMaps();

            const sessionToken = new google.maps.places.AutocompleteSessionToken();
            const autocomplete = new google.maps.places.Autocomplete(inputElement, {
                ...options,
                sessionToken,
            });

            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();

                if (place.geometry) {
                    onPlaceSelected(place);
                }
            });

            return autocomplete;
        } catch (error) {
            Logger.error(ServiceType.WEB, "Autocomplete initialization error", {
                tag: LogTag.ERROR,
                error,
            });
            return null;
        }
    },

    generateAddressQuery: (place: any): string => {
        const { name, address_components } = place;

        const getComponent = (type: string): string => {
            const component = address_components?.find((comp: any) => comp.types.includes(type));
            return component?.long_name || "";
        };

        const route = getComponent("route");
        const locality = getComponent("locality");
        const political =
            address_components?.find((comp: any) =>
                comp.types.includes("administrative_area_level_1")
            )?.short_name || "";

        return `${name} ${route}, ${locality}, ${political}`.trim();
    },
    cleanup: (): void => {
        geocodeCache.clear();
    },
};
